import NextLink from 'next/link';
import { useRouter } from 'next/router';
import React, { useMemo, useState } from 'react';
import { Grid, ListItemText, MenuItem } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useCoachingListCountQuery } from 'src/components/Layouts/Primary/CoachingListCount.generated';
import { useAccountListId } from 'src/hooks/useAccountListId';
import { useNavPages } from 'src/hooks/useNavPages';
import theme from 'src/theme';
import { useGetToolNotificationsQuery } from './GetToolNotifcations.generated';
import { NavMenuDropdown } from './NavMenuDropdown';

export const useStyles = makeStyles()(() => ({
  navListItem: {
    order: 2,
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
    '&[aria-current=page]': {
      backgroundColor: theme.palette.mpdxGrayMedium.main,
      backgroundBlendMode: 'multiply',
    },
  },
  expand: {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
  subMenu: {
    backgroundImage: `linear-gradient(0deg, ${theme.palette.mpdxGrayDark.main}, ${theme.palette.mpdxGrayDark.main})`,
  },
  menuItemSelected: {
    backgroundBlendMode: 'multiply',
    backgroundColor: theme.palette.mpdxGrayMedium.main,
  },
  needsAttention: {
    backgroundImage: `linear-gradient(0deg, ${theme.palette.mpdxYellow.main}, ${theme.palette.mpdxYellow.main})`,
  },
  menuIcon: {
    marginRight: theme.spacing(1),
  },
  notificationBox: {
    backgroundColor: theme.palette.progressBarYellow.main,
    borderRadius: '10px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: theme.spacing(1),
    '&>.MuiTypography-root': {
      fontSize: '12px',
      whiteSpace: 'nowrap',
      fontWeight: '700',
      lineHeight: 1,
      minWidth: '10px',
      padding: '3px 7px',
    },
  },
  darkText: {
    color: theme.palette.mpdxGrayDark.main,
  },
  whiteText: {
    color: 'white',
  },
  menuItem: {
    paddingInline: '10px',
    '&:focus-visible, &:hover, &[aria-current=page]': {
      backgroundColor: theme.palette.mpdxGrayMedium.main,
      backgroundBlendMode: 'multiply',
    },
  },
}));

export enum ToolName {
  FixCommitmentInfo = 'fixCommitmentInfo',
  MailingAddresses = 'mailingAddresses',
  FixSendNewsletter = 'fixSendNewsletter',
  FixEmailAddresses = 'fixEmailAddresses',
  FixPhoneNumbers = 'fixPhoneNumbers',
  MergeContacts = 'mergeContacts',
  MergePeople = 'mergePeople',
}

const NavMenu: React.FC = () => {
  const accountListId = useAccountListId();

  const { classes } = useStyles();
  const { data, loading } = useGetToolNotificationsQuery({
    variables: { accountListId },
  });
  const { data: coachingData } = useCoachingListCountQuery();

  const isCoaching = !!coachingData?.coachingAccountLists?.totalCount;

  const toolData: { [key: string]: { totalCount: number } } = {
    [ToolName.FixCommitmentInfo]: data?.[ToolName.FixCommitmentInfo] ?? {
      totalCount: 0,
    },
    [ToolName.MailingAddresses]: data?.fixMailingAddresses ?? {
      totalCount: 0,
    },
    [ToolName.FixSendNewsletter]: data?.[ToolName.FixSendNewsletter] ?? {
      totalCount: 0,
    },
    [ToolName.FixEmailAddresses]: data?.[ToolName.FixEmailAddresses] ?? {
      totalCount: 0,
    },
    [ToolName.FixPhoneNumbers]: data?.[ToolName.FixPhoneNumbers] ?? {
      totalCount: 0,
    },
    [ToolName.MergeContacts]: data?.[ToolName.MergeContacts] ?? {
      totalCount: 0,
    },
    [ToolName.MergePeople]: data?.[ToolName.MergePeople] ?? { totalCount: 0 },
  };

  const sum = useMemo<number>(() => {
    return data
      ? Object.values(toolData).reduce(
          (sum, toolContacts) => sum + toolContacts.totalCount,
          0,
        )
      : 0;
  }, [loading]);

  const [reportsMenuOpen, setReportsMenuOpen] = useState(false);
  const [hrToolsMenuOpen, setHrToolsMenuOpen] = useState(false);
  const [toolsMenuOpen, setToolsMenuOpen] = useState(false);

  const handleReportsMenuToggle = () => {
    setReportsMenuOpen((prevOpen) => !prevOpen);
    handleToolsMenuClose();
  };

  const handleReportsMenuClose = () => {
    setReportsMenuOpen(false);
  };

  const handleHrToolsMenuToggle = () => {
    setHrToolsMenuOpen((prevOpen) => !prevOpen);
    handleToolsMenuClose();
  };

  const handleHrToolsMenuClose = () => {
    setHrToolsMenuOpen(false);
  };

  const handleToolsMenuToggle = () => {
    setToolsMenuOpen((prevOpen) => !prevOpen);
    handleReportsMenuClose();
  };

  const handleToolsMenuClose = () => {
    setToolsMenuOpen(false);
  };
  const { pathname } = useRouter();

  const { navPages } = useNavPages(isCoaching);
  const reports = navPages.find((page) => page.id === 'reports-page');
  const hrTools = navPages.find((page) => page.id === 'hr-tools-page');
  const mpdxTools = navPages.find((page) => page.id === 'mpdx-tools-page');

  const coachingIndex = navPages.findIndex(
    (page) => page.id === 'coaching-page',
  );

  return (
    <Grid container alignItems="center" size="auto">
      {navPages.map(
        (page) =>
          page.isDropdown === false && (
            <Grid key={page.id} className={classes.navListItem}>
              <MenuItem
                component={NextLink}
                href={page.href ?? ''}
                tabIndex={0}
                className={classes.menuItem}
                aria-current={pathname === page.pathname ? 'page' : undefined}
              >
                <ListItemText primary={page.title} />
              </MenuItem>
            </Grid>
          ),
      )}

      {reports && (
        <NavMenuDropdown
          page={reports}
          menuOpen={reportsMenuOpen}
          handleMenuToggle={handleReportsMenuToggle}
          handleMenuClose={handleReportsMenuClose}
          testId="ReportMenuToggle"
          sum={sum}
          toolData={toolData}
          loading={loading}
          isTool={false}
        />
      )}
      {hrTools && (
        <NavMenuDropdown
          page={hrTools}
          menuOpen={hrToolsMenuOpen}
          handleMenuToggle={handleHrToolsMenuToggle}
          handleMenuClose={handleHrToolsMenuClose}
          testId="HrToolsMenuToggle"
          sum={sum}
          toolData={toolData}
          loading={loading}
          isTool={false}
        />
      )}
      {mpdxTools && (
        <NavMenuDropdown
          page={mpdxTools}
          menuOpen={toolsMenuOpen}
          handleMenuToggle={handleToolsMenuToggle}
          handleMenuClose={handleToolsMenuClose}
          testId="ToolsMenuToggle"
          sum={sum}
          toolData={toolData}
          loading={loading}
          isTool={true}
        />
      )}

      {isCoaching && (
        <Grid className={classes.navListItem}>
          <MenuItem
            component={NextLink}
            href={navPages[coachingIndex].href ?? ''}
            tabIndex={0}
            className={classes.menuItem}
            aria-current={
              pathname === navPages[coachingIndex].pathname ? 'page' : undefined
            }
          >
            <ListItemText primary={navPages[coachingIndex].title} />
          </MenuItem>
        </Grid>
      )}
    </Grid>
  );
};

export default NavMenu;
